import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import OpenLayersMap from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import ImageLayer from "ol/layer/Image";
import VectorSource from "ol/source/Vector";
import XYZ from "ol/source/XYZ";
import { TileJSON } from "ol/source";
import ImageStatic from "ol/source/ImageStatic";
import GeoJSON from "ol/format/GeoJSON";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import { fromLonLat } from "ol/proj";
import { Circle as CircleStyle, Fill, Stroke, Style } from "ol/style";
import { isEmpty as isEmptyExtent } from "ol/extent";
import Spinner from "@components/Spinner";
import * as fieldsService from "@services/fieldsService";
import updateFeatureStyle from "@screens/FieldsScreen/fieldsScreenUtils";
import type { Disagreement } from "@services/reportsService";

const geoOpts = { dataProjection: "EPSG:4326", featureProjection: "EPSG:3857" };

const urlTempl = process.env.REACT_APP_HIGH_RES_SATELITE_API_URL_TEMPLATE!;
const lowResUrl = process.env.REACT_APP_LOW_RES_SATELITE_API_URL!;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin: 8px 0 6px;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #eceef0;
`;

const PanelLabel = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 12px;
  font-weight: 700;
  color: #2b2f38;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.12);
`;

const MapDiv = styled.div`
  width: 100%;
  height: 300px;
  background: #dfe3e6;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  z-index: 3;
`;

const MarkerHint = styled.div`
  font-size: 11px;
  color: #899093;
  margin: 2px 0 18px;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
`;

const Dot = styled.span<{ c: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ c }) => c};
  margin-right: 5px;
  vertical-align: middle;
  border: 1px solid #fff;
`;

const UNDER = "#e23b3b"; // high predictor, low yield
const OVER = "#2f6fe2"; // low predictor, high yield

function baseLayers() {
  return [
    new TileLayer({
      source: new TileJSON({ url: lowResUrl, tileSize: 512, crossOrigin: "anonymous" }),
      maxZoom: 13,
    }),
    new TileLayer({ source: new XYZ({ url: urlTempl, maxZoom: 18 }), minZoom: 12 }),
  ];
}

function markerLayer(disagreements: Disagreement[]): VectorLayer<VectorSource> {
  const src = new VectorSource();
  for (const d of disagreements) {
    if (typeof d.lat !== "number" || typeof d.lng !== "number") continue;
    const f = new Feature({ geometry: new Point(fromLonLat([d.lng, d.lat])) });
    f.set("gmaps", `https://www.google.com/maps?q=${d.lat},${d.lng}`);
    f.setStyle(
      new Style({
        image: new CircleStyle({
          radius: 6,
          fill: new Fill({ color: d.kind === "underperform" ? UNDER : OVER }),
          stroke: new Stroke({ color: "#fff", width: 1.5 }),
        }),
      })
    );
    src.addFeature(f);
  }
  return new VectorLayer({ source: src, zIndex: 10 });
}

/** One small OL map. Returns a disposer. */
function mountMap(
  el: HTMLDivElement,
  dataLayer: VectorLayer<VectorSource> | ImageLayer<ImageStatic> | null,
  markers: VectorLayer<VectorSource> | null,
  extent: number[] | null
): () => void {
  const layers: any[] = baseLayers();
  if (dataLayer) layers.push(dataLayer);
  if (markers) layers.push(markers);
  const map = new OpenLayersMap({
    target: el,
    layers,
    controls: [],
    view: new View({ center: fromLonLat([0, 0]), zoom: 2, maxZoom: 20 }),
  });
  if (extent && !isEmptyExtent(extent)) {
    map.getView().fit(extent, { padding: [16, 16, 16, 16], maxZoom: 17 });
  }
  // Click a marker → open Google Maps.
  const onClick = (e: any) => {
    map.forEachFeatureAtPixel(e.pixel, (feat) => {
      const url = feat.get("gmaps");
      if (url) {
        window.open(url, "_blank", "noopener");
        return true;
      }
    });
  };
  map.on("singleclick", onClick);
  setTimeout(() => map.updateSize(), 80);
  return () => {
    map.un("singleclick", onClick);
    map.setTarget(undefined);
  };
}

interface Props {
  fieldId: string;
  seasonId: string | null;
  predictorLayer: "productivity" | "ndvi" | string;
  predictorLabel: string;
  outcomeLabel: string;
  ndviDate?: string | null;
  disagreements: Disagreement[];
}

const ReportMaps: React.FC<Props> = ({
  fieldId,
  seasonId,
  predictorLayer,
  predictorLabel,
  outcomeLabel,
  ndviDate,
  disagreements,
}) => {
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let disposers: Array<() => void> = [];
    let objectUrl: string | null = null;
    let cancelled = false;

    (async () => {
      try {
        const format = new GeoJSON();

        // Right map: yield heatmap + disagreement markers.
        const yieldRes = seasonId
          ? await fieldsService.getHeatmapData(fieldId, seasonId)
          : null;
        let extent: number[] | null = null;
        let yieldLayer: VectorLayer<VectorSource> | null = null;
        if (yieldRes?.features) {
          const src = new VectorSource();
          const feats = format.readFeatures(yieldRes.features, geoOpts) as Feature[];
          feats.forEach((f) => updateFeatureStyle(f as any));
          src.addFeatures(feats as any);
          yieldLayer = new VectorLayer({ source: src });
          const ex = src.getExtent();
          if (!isEmptyExtent(ex)) extent = ex;
        }

        // Left map: predictor layer.
        let leftLayer: VectorLayer<VectorSource> | ImageLayer<ImageStatic> | null = null;
        if (predictorLayer === "productivity") {
          const prod = await fieldsService.getProductivityMap(fieldId);
          if (prod?.geojson) {
            const src = new VectorSource();
            const feats = format.readFeatures(prod.geojson, geoOpts) as Feature[];
            feats.forEach((f) => updateFeatureStyle(f as any));
            src.addFeatures(feats as any);
            leftLayer = new VectorLayer({ source: src });
            if (!extent) {
              const ex = src.getExtent();
              if (!isEmptyExtent(ex)) extent = ex;
            }
          }
        } else if (predictorLayer === "ndvi" && ndviDate) {
          objectUrl = await fieldsService.getNdviImageUrl(fieldId, ndviDate, false);
          if (extent) {
            leftLayer = new ImageLayer({
              source: new ImageStatic({ url: objectUrl, imageExtent: extent }),
              opacity: 0.9,
            });
          }
        }

        if (cancelled) return;
        if (leftRef.current) {
          disposers.push(
            mountMap(leftRef.current, leftLayer, null, extent)
          );
        }
        if (rightRef.current) {
          disposers.push(
            mountMap(rightRef.current, yieldLayer, markerLayer(disagreements), extent)
          );
        }
        setLoading(false);
      } catch (e) {
        if (!cancelled) {
          console.warn("ReportMaps: failed to render", e);
          setFailed(true);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      disposers.forEach((d) => d());
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fieldId, seasonId, predictorLayer, ndviDate, disagreements]);

  if (failed) return null;

  return (
    <>
      <Grid>
        <Panel>
          <PanelLabel>{predictorLabel}</PanelLabel>
          <MapDiv ref={leftRef} />
          {loading && (
            <Overlay>
              <Spinner />
            </Overlay>
          )}
        </Panel>
        <Panel>
          <PanelLabel>{outcomeLabel}</PanelLabel>
          <MapDiv ref={rightRef} />
          {loading && (
            <Overlay>
              <Spinner />
            </Overlay>
          )}
        </Panel>
      </Grid>
      {disagreements.some((d) => typeof d.lat === "number") && (
        <MarkerHint>
          <span>
            <Dot c={UNDER} /> Underperforming (high {predictorLabel.toLowerCase()}, low yield)
          </span>
          <span>
            <Dot c={OVER} /> Overperforming (low {predictorLabel.toLowerCase()}, high yield)
          </span>
          <span style={{ color: "#aeb3b9" }}>Click a dot to open Google Maps</span>
        </MarkerHint>
      )}
    </>
  );
};

export default ReportMaps;
